import { z } from "zod"

import { isValidDomain, normalizeDomain } from "@productlane/lib/src/domain"
import {
	AlertDialog,
	Badge,
	Button,
	CopyButton,
	Form,
	FormInput,
	Loader,
	useZodForm,
} from "@productlane/ui"

import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { dnsHostLabel } from "@/screens/settings/settings-pages/domain-progress"

const STATUS_POLL_INTERVAL = 15000

const DomainFormSchema = z.object({
	domain: z
		.string()
		.min(1, "Enter a domain")
		.refine((value) => isValidDomain(normalizeDomain(value)), {
			message: "Enter a bare domain like docs.example.com",
		}),
})

type DomainState = "active" | "waiting_dns" | "waiting_certificate" | "error"

const STATE_BADGE: Record<
	DomainState,
	{ label: string; variant: "green" | "amber" | "red" | "default" }
> = {
	active: { label: "Live", variant: "green" },
	waiting_dns: { label: "Waiting for DNS", variant: "amber" },
	waiting_certificate: { label: "Issuing certificate", variant: "amber" },
	error: { label: "Error", variant: "red" },
}

/** The "Own domain" side of a portal's address: add a domain, or follow it to live. */
export function PortalDomainBody({
	portalInstanceId,
	customDomain,
	onRemove,
}: {
	portalInstanceId: string
	customDomain: string | null
	onRemove: () => void
}) {
	const utils = api.useUtils()

	const domainQuery = api.workspace.getPortalInstanceDomainInfo.useQuery(
		{ portalInstanceId },
		{
			enabled: !!customDomain,
			refetchInterval: (data) =>
				data?.summary.state === "active" ? false : STATUS_POLL_INTERVAL,
			retry: false,
		},
	)

	const setDomain = api.workspace.setPortalInstanceDomain.useMutation({
		onSuccess: async () => {
			await utils.workspace.getPortalInstanceDomainInfo.invalidate()
			toast.success({
				title: "Domain added, now add the CNAME record",
				description: "The portal goes live once the record resolves.",
			})
		},
		onError: (error) => {
			toast.error({
				title: "Your domain was not added",
				description: error.message,
			})
		},
	})

	const form = useZodForm({
		schema: DomainFormSchema,
		defaultValues: { domain: "" },
	})

	if (!customDomain) {
		return (
			<Form
				form={form}
				onSubmit={(values) =>
					setDomain.mutate({
						portalInstanceId,
						domain: normalizeDomain(values.domain),
					})
				}
				className="flex items-start gap-2"
			>
				<FormInput
					name="domain"
					className="flex-1"
					placeholder="docs.example.com"
				/>
				<Button type="submit" isLoading={setDomain.isLoading}>
					Add
				</Button>
			</Form>
		)
	}

	const info = domainQuery.data
	const state = info?.summary.state
	const badge = state ? STATE_BADGE[state] : null

	return (
		<div className="flex flex-col gap-3">
			<div className="flex items-center gap-2">
				<span className="truncate font-mono text-sm">{customDomain}</span>
				{domainQuery.isLoading ? (
					<Loader />
				) : badge ? (
					<Badge variant={badge.variant}>{badge.label}</Badge>
				) : domainQuery.isError ? (
					<Badge variant="default">Unknown</Badge>
				) : null}
				<div className="flex-1" />
				<Button variant="outline" onClick={onRemove}>
					Remove
				</Button>
			</div>

			{domainQuery.isError ? (
				<div className="flex flex-col gap-1">
					<div className="text-sm font-medium text-primary">
						{"We couldn't check this domain"}
					</div>
					<div className="text-sm text-secondary">
						{
							"We'll try again in a moment. If it stays like this, remove the domain and add it again."
						}
					</div>
				</div>
			) : null}

			{info && state !== "active" ? (
				<div className="flex flex-col gap-1">
					<div className="text-sm font-medium text-primary">
						{info.summary.title}
					</div>
					<div className="text-sm text-secondary">{info.summary.detail}</div>
				</div>
			) : null}

			{info && state === "waiting_dns" && info.expectedCname ? (
				<div className="space-y-2 rounded-lg bg-gray-100 p-3 dark:bg-gray-800">
					<DnsRow label="Type" value="CNAME" />
					<DnsRow label="Host" value={dnsHostLabel(info.hostname)} copyable />
					<DnsRow label="Value" value={info.expectedCname} copyable />
				</div>
			) : null}
		</div>
	)
}

/** Confirms dropping a portal's domain, which sends visitors back to the path. */
export function RemovePortalDomainDialog({
	portalInstanceId,
	customDomain,
	fallbackUrl,
	isOpen,
	onOpenChange,
}: {
	portalInstanceId: string
	customDomain: string
	fallbackUrl: string
	isOpen: boolean
	onOpenChange: (open: boolean) => void
}) {
	const resetDomain = api.workspace.resetPortalInstanceDomain.useMutation({
		onSuccess: () => {
			toast.success({ title: "Domain removed" })
		},
		onError: (error) => {
			toast.error({
				title: "Your domain was not removed",
				description: error.message,
			})
		},
	})

	return (
		<AlertDialog
			isOpen={isOpen}
			onOpenChange={onOpenChange}
			variant="positive"
			title={`Remove ${customDomain}?`}
			description={`This portal goes back to ${fallbackUrl}. Links to the domain stop working.`}
			confirmText="Remove domain"
			onCancel={() => onOpenChange(false)}
			onConfirm={() => {
				onOpenChange(false)
				resetDomain.mutate({ portalInstanceId })
			}}
		/>
	)
}

function DnsRow({
	label,
	value,
	copyable,
}: {
	label: string
	value: string
	copyable?: boolean
}) {
	return (
		<div className="flex items-center">
			<div className="w-1/3 shrink-0 text-sm text-secondary">{label}</div>
			<div className="flex w-2/3 min-w-0 items-center gap-2">
				<div className="truncate font-mono text-sm">{value}</div>
				{copyable ? (
					<CopyButton
						value={value}
						className="!text-tertiary hover:!text-primary"
					/>
				) : null}
			</div>
		</div>
	)
}
